/**
 * LAS FECHAS DE TODA LA APP, en un solo formato (revision del 7-oct).
 *
 * Antes cada lista tenia el suyo: «2026-10-07» en las llamadas, «7 oct, 19:34» en los
 * correos, «mar, 29 sept, 11:00» en las reuniones (Intl da «sept»), «6-oct» y «LUN 5-OCT»
 * en el Seguimiento, y en las llamadas relevantes «10h» pegado a la duracion se leia como
 * una duracion. Joan pidio coherencia en las fechas: un formato de dia, uno de dia y hora,
 * y «hace» escrito.
 *
 *   dia          «7 oct»            (un dia, sin hora)
 *   diaSemana    «mié 7 oct»        (cabeceras de dia, caducidades)
 *   diaHora      «mié 7 oct, 19:34» (una cosa con su hora)
 *   hace         «hace 10 h»        (lo reciente; nunca negativo: lo futuro es «ahora»)
 *
 * Todo en la zona del inquilino (Madrid por defecto), con los meses escritos aqui: Intl
 * pone «sept» en septiembre y el resto de la app dice «sep».
 */

export const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'] as const;
export const DIAS_SEMANA = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'] as const;
const ZONA = 'Europe/Madrid';

const formatos = new Map<string, Intl.DateTimeFormat>();
function formato(zona: string): Intl.DateTimeFormat {
  let f = formatos.get(zona);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-GB', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    } catch {
      f = new Intl.DateTimeFormat('en-GB', { timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    }
    formatos.set(zona, f);
  }
  return f;
}

/** La hora de pared de un instante en una zona: dia AAAA-MM-DD, hora y minuto. */
export function enZona(instante: Date | string | number, zona = ZONA): { dia: string; hora: number; minuto: number } | null {
  const t = instante instanceof Date ? instante : new Date(instante);
  if (Number.isNaN(t.getTime())) return null;
  const p = Object.fromEntries(formato(zona).formatToParts(t).map((x) => [x.type, x.value]));
  return { dia: `${p.year}-${p.month}-${p.day}`, hora: Number(p.hour) % 24, minuto: Number(p.minute) };
}

const DIA_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** El dia civil de un AAAA-MM-DD o de un instante (en la zona). */
function diaDe(x: string | Date, zona: string): string | null {
  if (typeof x === 'string' && DIA_RE.test(x)) return x;
  return enZona(x, zona)?.dia ?? null;
}

/** «7 oct». */
export function dia(x: string | Date, zona = ZONA): string {
  const d = diaDe(x, zona);
  if (!d) return '-';
  return `${Number(d.slice(8, 10))} ${MESES[Number(d.slice(5, 7)) - 1]}`;
}

/** «mié 7 oct». */
export function diaSemana(x: string | Date, zona = ZONA): string {
  const d = diaDe(x, zona);
  if (!d) return '-';
  const semana = new Date(`${d}T12:00:00Z`).getUTCDay();
  return `${DIAS_SEMANA[semana]} ${dia(d)}`;
}

/** «19:34». */
export function hora(x: string | Date, zona = ZONA): string {
  const p = enZona(x, zona);
  return p ? `${p.hora}:${String(p.minuto).padStart(2, '0')}` : '-';
}

/** «mié 7 oct, 19:34». */
export function diaHora(x: string | Date, zona = ZONA): string {
  const p = enZona(x, zona);
  return p ? `${diaSemana(p.dia)}, ${hora(x, zona)}` : '-';
}

/** «hace 5 min», «hace 3 h», «hace 2 d»; lo de hace menos de un minuto y lo futuro, «ahora». */
export function hace(x: string | Date, ahora: Date = new Date()): string {
  const t = (x instanceof Date ? x : new Date(x)).getTime();
  if (!Number.isFinite(t)) return '-';
  const min = Math.floor((ahora.getTime() - t) / 60_000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}
